import { Link } from "react-router-dom";
import { LessonCard } from "@/components/engagement/Lessons";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { OneLine } from "@/components/ui/one-line";
import { fmtUsd } from "@/lib/format";
import { useMilestoneCelebrations, useMilestones, useRecap } from "@/hooks/useEngagement";
import { nextStep, type Milestone, type NextStep } from "@/lib/engagement.core";
import { useAssetName } from '@/lib/instruments';
import { focusLink } from '@/lib/focusLink.core';

/****************
 * THE TOP OF THE DASHBOARD: WHAT HAPPENED, WHERE YOU ARE, WHAT'S NEXT.
 * 2026-10-21, engagement redesign.
 *
 * A reason worth one minute a day: since you were away (3 lines), one big
 * number, the milestone journey with the next one in view, the check-ins this
 * week (never a streak to lose), and ONE next step. Everything else on the
 * Dashboard sits below, in sections the user opens.
 *
 * Twin on the phone: mobile-app/src/components/data/TodayPanel.tsx.
 ****************/

export function TodayPanel({ inCard = false }: { inCard?: boolean } = {}) {
  const { t } = useTranslation("dashboard");
  const recap = useRecap();
  const ms = useMilestones();

  useMilestoneCelebrations((m: Milestone) =>
    toast.success(t(`milestone.${m.key}.title`), { description: t(`milestone.${m.key}.done`), duration: 6000 }),
  );

  const step = nextStep(ms.data, recap.data);

  /*
   * 2026-10-25 (second pass), Guillaume: "Since you were away" now lives INSIDE
   * "Your performance" as its first period (PortfolioSection), and the
   * achievements moved to the header's trophy (AchievementsTray). What stays
   * here: the check-ins this week, the ONE next step and the lesson.
   */
  return (
    <section className={inCard ? "mt-5 border-t border-border/40 pt-4" : "mb-6"} aria-label={t("today.label")}>
      <div className="grid gap-4 lg:grid-cols-2">
        <NextStepCard step={step} />
        <LessonCard />
      </div>
      {/* The check-ins line lives under the performance figures only (Guillaume 11-02:
          it showed twice; components/engagement/FollowedAchievements.tsx CheckinsThisWeek). */}
    </section>
  );
}

function NextStepCard({ step }: { step: NextStep | null }) {
  const { t } = useTranslation("dashboard");
  const an = useAssetName();
  if (!step) return null;
  const target = step.kind === "getStarted" ? "/get-started"
    : step.kind === "learnLoss" ? focusLink({ kind: 'trade', sessionId: step.trade.subscription_id, tradeId: step.trade.id, why: true }, 'web')
      : step.kind === "tryLive" ? "/copy-trade" : "/sessions";
  const text = step.kind === "learnLoss" ? t("next.learnLoss", { symbol: an(step.trade.symbol) })
    : step.kind === "tryLive" ? t("next.tryLiveRecord", { n: step.closed, wins: step.wins, pnl: fmtUsd(step.netPnl, 2, { sign: true }) })
      : t(`next.${step.kind}`);
  return (
    <Link
      to={target}
      className="glass-card rounded-xl p-5 flex items-center gap-4 group hover:border-primary/40 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
        <Sparkle size={20} weight="fill" className="text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <OneLine className="text-xs uppercase tracking-wider text-muted-foreground">{t("today.nextStep")}</OneLine>
        <p className="text-sm font-medium">{text}</p>
      </div>
      <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground shrink-0" />
    </Link>
  );
}
